// app/javascript/controllers/pages/controllers/carousel_controller.js
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['slide', 'prev', 'next', 'dots', 'slideContainer', 'caption'];
  static values = { interval: { type: Number, default: 3000 } };

  connect() {
    this.currentIndex = 0;
    this.updateCarousel();
    this.createDots();
    this.startAutoSlide();

    this.resizeObserver = new ResizeObserver(() => {
      this.updateCarousel();
      // Restart auto-slide to check screen size
      this.startAutoSlide();
    });
    this.resizeObserver.observe(this.element);
  }

  disconnect() {
    this.stopAutoSlide();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  startAutoSlide() {
    this.stopAutoSlide();
    // Only auto-slide on mobile (screen width < 768px)
    if (this.slideTargets.length > 1 && window.innerWidth < 768) {
      this.autoSlideTimer = setInterval(() => {
        this.next();
      }, this.intervalValue);
    }
  }

  stopAutoSlide() {
    if (this.autoSlideTimer) {
      clearInterval(this.autoSlideTimer);
      this.autoSlideTimer = null;
    }
  }

  pauseAutoSlide() {
    this.stopAutoSlide();
  }

  resumeAutoSlide() {
    this.startAutoSlide();
  }

  prev() {
    this.currentIndex =
      (this.currentIndex - 1 + this.slideTargets.length) %
      this.slideTargets.length;
    this.updateCarousel();
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.slideTargets.length;
    this.updateCarousel();
  }

  updateCarousel() {
    const slideWidth = this.slideTargets[0].offsetWidth;
    const offset = this.currentIndex * slideWidth;
    this.slideContainerTarget.style.transform = `translateX(-${offset}px)`;
    this.updateDots();
    this.updateCaptions();
  }

  updateCaptions() {
    // Update caption visibility if captions exist
    if (this.hasCaptionTarget) {
      this.captionTargets.forEach((caption, index) => {
        if (index === this.currentIndex) {
          caption.classList.remove('hidden');
        } else {
          caption.classList.add('hidden');
        }
      });
    }
  }

  createDots() {
    // Clear existing dots first
    if (this.hasDotsTarget) {
      this.dotsTarget.innerHTML = '';
    }
    
    this.slideTargets.forEach((_, index) => {
      const dot = document.createElement('div');
      dot.classList.add(
        'carousel-dot',
        'w-2',
        'h-2',
        'rounded-full',
        'bg-gray-300',
        'cursor-pointer',
        'transition-colors',
        'duration-200'
      );
      dot.dataset.action = 'click->carousel#goToSlide';
      dot.dataset.carouselIndex = index;
      if (this.hasDotsTarget) {
        this.dotsTarget.appendChild(dot);
      }
    });
    this.updateDots();
  }

  updateDots() {
    if (this.hasDotsTarget) {
      this.dotsTarget.querySelectorAll('.carousel-dot').forEach((dot, index) => {
        dot.classList.toggle('bg-gray-600', index === this.currentIndex);
        dot.classList.toggle('bg-gray-300', index !== this.currentIndex);
      });
    }
  }

  goToSlide(event) {
    this.currentIndex = parseInt(event.currentTarget.dataset.carouselIndex);
    this.updateCarousel();
    // Restart auto-slide when user interacts
    this.startAutoSlide();
  }
}
